import { useQuery } from "@apollo/client";
import { withJsonFormsControlProps } from "@jsonforms/react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Radio,
  Typography,
} from "@mui/material";
import { Button, Dropdown, InputItem, LoadingSpinner } from "pepsico-ds";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { CampaignSetupContext } from "../../../context/CampaignSetupContext";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import { listPrizeSeeds } from "../../../graphql/queries/instantWinQueries";
import { useWinningSchedulesHook } from "../../../hooks/useWinningSchedulesHook";
import { convertDateToLocaleTz, formatDateTz } from "../../../utils/commons";
import DatePickerMaterialUI from "../../common/DatePickerMaterialUI/DatePickerMaterialUI";
import "./WinningScheduleSelect.scss";

const getUTCDateKey = (d) => {
  try {
    const dt = new Date(d);
    return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
  } catch (e) {
    console.log(e);
    return String(d);
  }
};

const isScheduleMismatchingCampaignDates = (
  activeStartDate,
  activeEndDate,
  schedule
) => {
  const campaignStartKey = getUTCDateKey(activeStartDate);
  const campaignEndKey = getUTCDateKey(activeEndDate);
  const scheduleStartKey = getUTCDateKey(schedule?.startsAt);
  const scheduleEndKey = getUTCDateKey(schedule?.endsAt);
  return campaignStartKey !== scheduleStartKey || campaignEndKey !== scheduleEndKey;
};

const WinningScheduleSelect = (props) => {
  const { handleChange, path, data } = props || {};
  const { programTimezone } = useContext(GlobalConfigContext);
  const {
    campaignState: { activeStartDate, activeEndDate },
  } = useContext(CampaignSetupContext);

  const { allList, loading } = useWinningSchedulesHook();

  // UI state
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState(null);
  const [pendingItem, setPendingItem] = useState(null);
  const [filters, setFilters] = useState({
    title: "",
    startsAt: "",
    endsAt: "",
    rollover: "",
  });
  const idRef = useRef(null);

  useEffect(() => {
    if (data) {
      setSelectedIds(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleItems = useMemo(() => {
    const base = allList.filter((it) =>
      ["DRAFT", "ACTIVE"].includes(it?.instantWinStatus)
    );
    return base.filter((it) => {
      // title contains
      if (filters.title) {
        const t = filters.title.toLowerCase();
        if (!it.title?.toLowerCase().includes(t)) return false;
      }
      // startsAt >= filter.startsAt
      if (filters.startsAt) {
        try {
          if (new Date(it.startsAt).getTime() < new Date(filters.startsAt).getTime())
            return false;
        } catch (e) {
          console.log(e);
        }
      }
      // endsAt <= filter.endsAt
      if (filters.endsAt) {
        try {
          if (new Date(it.endsAt).getTime() > new Date(filters.endsAt).getTime())
            return false;
        } catch (e) {
          console.log(e);
        }
      }
      // rollover equals
      if (filters.rollover) {
        const rollover = filters.rollover?.[0]?.id;
        if (it.rollover !== rollover) return false;
      }
      return true;
    });
  }, [allList, filters]);

  const [pendingPrizeSeedsTotal, setPendingPrizeSeedsTotal] = useState(null);
  const [isFetchingPrizeSeeds, setIsFetchingPrizeSeeds] = useState(false);
  const [prizeSeedsCache, setPrizeSeedsCache] = useState({});
  const [fetchingPrizeSeedsId, setFetchingPrizeSeedsId] = useState(null);

  const fetchPrizeSeedsTotal = async (item) => {
    // Serve from cache when available to avoid re-fetching
    if (typeof prizeSeedsCache[item.id] === "number") {
      return prizeSeedsCache[item.id];
    }

    setIsFetchingPrizeSeeds(true);
    setFetchingPrizeSeedsId(item.id);
    try {
      const { data: prizeData } = await refetchPrizeSeeds({
        instantWinId: item.id,
        page: 1,
        size: 1,
        filter: {},
      });
      const total = prizeData?.listPrizeSeeds?.total ?? 0;
      setPrizeSeedsCache((prev) => ({ ...prev, [item.id]: total }));
      return total;
    } catch (e) {
      console.log(e);
      return 0;
    } finally {
      setIsFetchingPrizeSeeds(false);
      setFetchingPrizeSeedsId(null);
    }
  };

  const onSelected = async (item) => {
    const requiresDateConfirmation = isScheduleMismatchingCampaignDates(
      activeStartDate,
      activeEndDate,
      item
    );

    if (selectedIds === item.id) {
      return;
    }

    try {
      if (requiresDateConfirmation) {
        idRef.current = item.id;
        setPendingItem(item);
        setPendingPrizeSeedsTotal(null);
        setShowModal(true);
      }

      const prizeSeedsTotal = await fetchPrizeSeedsTotal(item);

      const hasNoPrizeSeeds = prizeSeedsTotal === 0;
      if (hasNoPrizeSeeds || requiresDateConfirmation) {
        idRef.current = item.id;
        setPendingItem(item);
        setPendingPrizeSeedsTotal(prizeSeedsTotal);
        setShowModal(true);
        return;
      }

      idRef.current = item.id;
      handleChange?.(path, item.id);
      setSelectedIds(item.id);
    } catch (e) {
      console.log(e);
    }
  };

  const { refetch: refetchPrizeSeeds } = useQuery(listPrizeSeeds, {
    variables: { instantWinId: "", page: 1, size: 1, filter: {} },
    skip: true,
  });

  useEffect(() => {
    if (!showModal) {
      setIsFetchingPrizeSeeds(false);
      setFetchingPrizeSeedsId(null);
    }
  }, [showModal]);

  const handleCloseModal = () => {
    setShowModal(false);
    setPendingItem(null);
    setPendingPrizeSeedsTotal(null);
    setIsFetchingPrizeSeeds(false);
    setFetchingPrizeSeedsId(null);
  };

  const onConfirm = () => {
    try {
      setSelectedIds(idRef.current);
      handleChange?.(path, idRef.current);
      setShowModal(false);
      setPendingItem(null);
      setPendingPrizeSeedsTotal(null);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className="add-winning-schedule-select-wrap">
      <div className="display-flex align-center mb-3">
        <h4 className="font-semibold mr-3">
          Add Winning Schedule:{" "}
          <span className="font-xs font-regular">
            {convertDateToLocaleTz(activeStartDate, programTimezone)} -{" "}
            {convertDateToLocaleTz(activeEndDate, programTimezone)}
          </span>
        </h4>
      </div>

      <div className="mt-3 display-flex align-center gap-3">
        <div className="container search-filter-ws-container p-0">
          <div className="search-wrapper">
            <InputItem
              placeholder="Type a search keyword..."
              trailingIcon="search"
              size="medium"
              className="input-search"
              onUpdate={(v) => setFilters((f) => ({ ...f, title: v || "" }))}
            />
          </div>
          <div className="filter-wrapper">
            <Dropdown
              childList={[
                { displayText: "Enabled - Across Days", id: "ENABLED" },
                {
                  displayText: "Enabled - Restrict to Same Day",
                  id: "ENABLED_RESTRICTED_DAY",
                },
                { displayText: "Disabled", id: "DISABLED" },
              ]}
              selection="single"
              selectedValue={filters.rollover}
              placeholder="Rollover"
              size="medium"
              className="filter-dropdown dropdown-segment"
              setSelectedValue={(val) => {
                setFilters((f) => ({ ...f, rollover: val }));
              }}
            />
            <DatePickerMaterialUI
              setDate={(date) =>
                setFilters((f) => ({
                  ...f,
                  startsAt: date ? new Date(date).toISOString() : "",
                }))
              }
              dateValue={filters.startsAt}
              placeholder="Start date"
              timezone={programTimezone || "America/Chicago"}
            />
            <DatePickerMaterialUI
              setDate={(date) =>
                setFilters((f) => ({
                  ...f,
                  endsAt: date ? new Date(date).toISOString() : "",
                }))
              }
              dateValue={filters.endsAt}
              placeholder="End date"
              timezone={programTimezone || "America/Chicago"}
            />
            <div
              className="clear-all-btn"
              onClick={() =>
                setFilters({ title: "", startsAt: "", endsAt: "", rollover: "" })
              }
              role="button"
              tabIndex="0"
            >
              Clear All
            </div>
          </div>
        </div>
      </div>

      <div>
        <table className="winning-schedules-list-table">
          <thead>
            <tr>
              <th></th>
              <th>TITLE</th>
              <th>DATE RANGE</th>
              <th>STATUS</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan="3" className="text-center">
                  <LoadingSpinner size={{ height: "40px", width: "40px" }} />
                </td>
              </tr>
            )}
            {!loading && visibleItems.length === 0 && (
              <tr>
                <td colSpan="3" className="font-bold text-center">
                  No winning schedules found
                </td>
              </tr>
            )}
            {visibleItems.map((item) => (
              <tr key={item.id}>
                <td>
                  {isFetchingPrizeSeeds && fetchingPrizeSeedsId === item.id ? (
                    <LoadingSpinner size={{ height: "24px", width: "24px" }} />
                  ) : (
                    <Radio
                      checked={selectedIds === item.id}
                      value={item.id}
                      onChange={() => onSelected(item)}
                      disableRipple
                      disableTouchRipple
                    />
                  )}
                </td>
                <td>
                  <p className="wrap-leaderboard-title" title={item.title}>
                    {item.title}
                  </p>
                </td>
                <td>
                  {item?.startsAt
                    ? convertDateToLocaleTz(item.startsAt, programTimezone, false)
                    : "-"}{" "}
                  -{" "}
                  {item?.endsAt
                    ? convertDateToLocaleTz(item.endsAt, programTimezone, false)
                    : "-"}
                </td>
                <td>{item.instantWinStatus}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog
        className="create-winning-schedules-modal"
        open={showModal}
        scroll="paper"
      >
        <DialogTitle>
          <Grid container justifyContent="space-between" direction="row">
            <Grid item xs={10}>
              <h2>
                {!!pendingItem?.id && pendingPrizeSeedsTotal === 0
                  ? "Cannot Add Winning Schedule"
                  : "Confirm to continue"}
              </h2>
            </Grid>
            <Grid item xs={1} display="flex" justifyContent="flex-end">
              <CloseIcon onClick={handleCloseModal} className="cursor-pointer" />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent sx={{ overflowX: "hidden" }}>
          <div className="my-3">
            {isFetchingPrizeSeeds ? (
              <div
                className="display-flex align-center gap-2"
                data-testid="prize-seed-loader"
              >
                <LoadingSpinner size={{ height: "24px", width: "24px" }} />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Checking winning moments...
                </Typography>
              </div>
            ) : !!pendingItem?.id && pendingPrizeSeedsTotal === 0 ? (
              <Typography severity="warning" sx={{ mt: 2 }}>
                The selected winning schedule has no winning moments configured. Add
                winning moments to this schedule before assigning it to the campaign.
              </Typography>
            ) : (
              <>
                <h5>
                  Winning Schedule period does not fully match the campaign period.
                </h5>
                <p className="font-xs my-2">
                  Campaign Period:{" "}
                  {formatDateTz(activeStartDate, programTimezone, "MM/DD/YYYY")} and{" "}
                  {formatDateTz(activeEndDate, programTimezone, "MM/DD/YYYY")}
                </p>
                <p className="font-xs my-2">
                  Winning Schedule Period:{" "}
                  {pendingItem?.startsAt
                    ? formatDateTz(
                        pendingItem.startsAt,
                        programTimezone,
                        "MM/DD/YYYY"
                      )
                    : "-"}{" "}
                  and{" "}
                  {pendingItem?.endsAt
                    ? formatDateTz(pendingItem.endsAt, programTimezone, "MM/DD/YYYY")
                    : "-"}
                </p>
              </>
            )}
          </div>
          {!(!!pendingItem?.id && pendingPrizeSeedsTotal === 0) && (
            <div className="mt-5 display-flex justify-content-end align-items-center">
              <Button
                onClick={handleCloseModal}
                variant="secondary"
                className="mr-3"
                size="medium"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={onConfirm}
                size="medium"
                disabled={isFetchingPrizeSeeds}
              >
                Use Winning Schedule Anyway
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const WinningScheduleSelectControl =
  withJsonFormsControlProps(WinningScheduleSelect);
export default WinningScheduleSelect;
